import React, { useEffect, useMemo, useState } from 'react';
import { api } from '../config/api';
import { Layers, Plus, Search, Edit, Trash2, Save, Loader2, ArrowLeft, RotateCcw } from 'lucide-react';
import toast from '../utils/toast';
import { isValidEmail, isValidPhoneNumber, normalizeEmailInput, normalizePhoneInput } from '../utils/formValidation';
import SearchBar from '../components/SearchBar';
import {
  masterAccentButton,
  masterHeaderCard,
  masterHeaderIcon,
  masterInput,
  masterInputLabel,
  masterPageShell,
  masterPanelPadding,
  masterPrimaryButton,
  masterSearchInput,
  masterSearchWrap,
  masterSelect,
  masterSubtitle,
  masterTitle,
  masterTableData,
  masterToolbar,
  masterTableID
} from '../components/masterPageStyles';

const emptyForm = {
  supplier_id: '',
  supplier_name: '',
  contact_person: '',
  mobile_number: '',
  email: '',
  village_town: '',
  po: '',
  ps: '',
  district: '',
  pin: '',
  state: '',
  GST_number: '',
  drug_license_number: '',
  payment_terms: '',
  status: 'Active'
};

const SupplierMaster = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [editId, setEditId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [formData, setFormData] = useState(emptyForm);
  const [showForm, setShowForm] = useState(true);

  const fetchItems = async () => {
    setLoading(true);
    try {
      const res = await api.get('/master/suppliers/all');
      if (res.data.success) setItems(res.data.data || []);
    } catch (err) {
      toast.error('Failed to load suppliers');
    } finally {
      setLoading(false);
    }
  };

  const fetchNextId = async () => {
    try {
      const res = await api.get('/master/suppliers/latest-id');
      if (res.data?.success) {
        setFormData((prev) => ({ ...prev, supplier_id: res.data.nextId }));
      }
    } catch (_) {}
  };

  useEffect(() => { fetchItems(); }, []);

  const filteredData = useMemo(() => {
    const sortedList = [...items].sort((a, b) => {
      const idA = String(a.supplier_id || a._id || '');
      const idB = String(b.supplier_id || b._id || '');
      return idB.localeCompare(idA, undefined, { numeric: true, sensitivity: 'base' });
    });

    const q = searchTerm.trim().toLowerCase();
    if (!q) return sortedList;

    return sortedList.filter((item) => {
      const supplierId = (item.supplier_id || '').toLowerCase();
      const supplierName = (item.supplier_name || '').toLowerCase();
      const contactPerson = (item.contact_person || '').toLowerCase();
      const mobileNumber = (item.mobile_number || '').toLowerCase();
      const gstNumber = (item.GST_number || '').toLowerCase();
      const dlNumber = (item.drug_license_number || '').toLowerCase();

      return (
        supplierId.startsWith(q) ||
        supplierName.startsWith(q) ||
        contactPerson.startsWith(q) ||
        mobileNumber.startsWith(q) ||
        gstNumber.startsWith(q) ||
        dlNumber.startsWith(q)
      );
    });
  }, [items, searchTerm]);

  const paginatedData = useMemo(() => {
    if (pageSize === -1) return filteredData;
    const start = (currentPage - 1) * pageSize;
    return filteredData.slice(start, start + pageSize);
  }, [filteredData, currentPage, pageSize]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, items.length, pageSize]);

  const handleEdit = (item) => {
    setEditId(item._id);
    setFormData({
      supplier_id: item.supplier_id,
      supplier_name: item.supplier_name,
      contact_person: item.contact_person || '',
      mobile_number: item.mobile_number || '',
      email: item.email || '',
      village_town: item.village_town || '',
      po: item.po || '',
      ps: item.ps || '',
      district: item.district || '',
      pin: item.pin || '',
      state: item.state || '',
      GST_number: item.GST_number || '',
      drug_license_number: item.drug_license_number || '',
      payment_terms: item.payment_terms || '',
      status: item.status || 'Active'
    });
    setShowForm(true);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'mobile_number') {
      setFormData((prev) => ({ ...prev, [name]: normalizePhoneInput(value) }));
      return;
    }
    if (name === 'email') {
      setFormData((prev) => ({ ...prev, [name]: normalizeEmailInput(value) }));
      return;
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.mobile_number && !isValidPhoneNumber(formData.mobile_number)) {
      return toast.error('Mobile number must be exactly 10 digits');
    }
    if (formData.email && !isValidEmail(formData.email)) {
      return toast.error('Enter a valid email address');
    }
    setIsSaving(true);
    try {
      const payload = { ...formData, id: editId };
      await api.post('/master/suppliers/upsert', payload);
      toast.success(editId ? 'Updated' : 'Saved');
      setEditId(null);
      setFormData(emptyForm);
      setShowForm(true);
      fetchItems();
    } catch (err) {
      const msg = err.response?.data?.message || 'Process failed';
      toast.error(msg);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this supplier?')) return;
    try {
      await api.delete(`/master/suppliers/${id}`);
      toast.success('Deleted');
      fetchItems();
    } catch (err) {
      toast.error('Delete failed');
    }
  };

  return (
    <div className={`${masterPageShell} p-1`}>
      <header className={`${masterHeaderCard} mb-1`}>
        <div className="flex items-center gap-1">
          <div className={masterHeaderIcon}><Layers size={10} /></div>
          <div>
            <h4 className={masterTitle}>Supplier Master</h4>
            
          </div>
        </div>

        {!showForm ? (
          <button
           onClick={() => { setEditId(null); 
            setShowForm(true);
            setFormData(emptyForm); }}
            className={masterAccentButton}
          >
            <Plus size={14} /> Add Supplier
          </button>
        ) : (
          <button
            onClick={() => setShowForm(false)}
            className={masterPrimaryButton}
          >
            <ArrowLeft size={14} /> VIEW LIST
          </button>
        )}
      </header>

      <main className="mx-auto max-w-7xl">
        {showForm ? (
          <form onSubmit={handleSubmit} className={`${masterPanelPadding} space-y-1`}>
            <div className="flex items-center justify-between border-b border-slate-300">
              <h4 className="text-sm font-bold text-slate-700 uppercase">{editId ? 'Edit Supplier' : 'New Supplier'}</h4>
              <button
                type="button"
                onClick={async () => {
                  setFormData(emptyForm);
                  await fetchNextId();
                }}
                className="text-[10px] font-bold text-rose-500 flex items-center gap-1 uppercase hover:underline"
              >
                <RotateCcw size={12}/> Reset
              </button>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-5 gap-x-1 gap-y-1">
              {/* <Input label="Supplier ID" name="supplier_id" value={formData.supplier_id} readOnly /> */}
              <Input label="Supplier Name" name="supplier_name" value={formData.supplier_name} onChange={handleChange} required />
              <Input label="Contact Person" name="contact_person" value={formData.contact_person} onChange={handleChange} />
              <Input label="Mobile Number" name="mobile_number" value={formData.mobile_number} onChange={handleChange} inputMode="numeric" maxLength={10} />
              <Input label="Email" name="email" value={formData.email} onChange={handleChange} />
              <Input label="GST Number" name="GST_number" value={formData.GST_number} onChange={handleChange} />
              <Input label="Drug License No" name="drug_license_number" value={formData.drug_license_number} onChange={handleChange} />
              <Input label="Payment Terms" name="payment_terms" value={formData.payment_terms} onChange={handleChange} />
              <Select label="Status" name="status" value={formData.status} onChange={handleChange} options={['Active', 'Inactive']} />
            </div>

            <div className="border-t border-stone-300 pt-1">
              <h3 className="mb-1 text-xs font-bold uppercase tracking-wide text-slate-700 border-b border-slate-300">Address</h3>
              <div className="grid grid-cols-2 md:grid-cols-6 gap-x-1 gap-y-1">
                <Input label="VILL/TOWN" name="village_town" value={formData.village_town} onChange={handleChange} />
                <Input label="PO" name="po" value={formData.po} onChange={handleChange} />
                <Input label="PS" name="ps" value={formData.ps} onChange={handleChange} />
                <Input label="DISTRICT" name="district" value={formData.district} onChange={handleChange} />
                <Input label="PIN" name="pin" value={formData.pin} onChange={handleChange} />
                <Input label="STATE" name="state" value={formData.state} onChange={handleChange} />
              </div>
            </div>

            <div className="pt-1 border-t flex justify-end">
              <button disabled={isSaving} type="submit" className={`${masterAccentButton} px-4 py-1`}>
                {isSaving ? <Loader2 className="animate-spin" size={16}/> : <Save size={16}/>}
                {editId ? 'Update' : 'Save'} Supplier
              </button>
            </div>
          </form>
        ) : (
         <div className="max-w-7xl mx-auto bg-white rounded-xl border border-slate-400 overflow-hidden shadow-sm">
                  <div>
              <SearchBar 
                value={searchTerm} 
                onChange={(val) => {
                  setSearchTerm(val);
                  setCurrentPage(1);
                }} 
                placeholder="Search alphabetically by supplier ID, name, contact, mobile, GST, or DL..." 
                totalItems={filteredData.length} 
                currentPage={currentPage} 
                pageSize={pageSize} 
                onPageChange={setCurrentPage} 
                onPageSizeChange={setPageSize} 
              />
            </div>

           
        <div className="overflow-x-auto">
            <table className="w-full min-w-[720px] text-left">
              <thead>
                  <tr>
                    <th className={masterTableData}>S.No</th>
                    <th className={masterTableData}>ID</th>
                    <th className={masterTableData}>Supplier</th>
                    <th className={masterTableData}>Contact Name</th>
                    <th className={masterTableData}>Mobile</th>
                    <th className={masterTableData}>GST</th>
                    <th className={masterTableData}>DL No</th>
                    <th className={masterTableData}>Status</th>
                    <th className={masterTableData}>Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-300 font-bold text-slate-600">
                  {loading ? (
                    <tr>
                      <td colSpan="9" className="p-12 text-center">Syncing Data...</td></tr>
                  ) : paginatedData.length > 0 ? paginatedData.map((item, index) => (
                    <tr key={item._id} className="hover:bg-emerald-50/30">
                      <td className={masterTableData}>{pageSize === -1 ? index + 1 : (currentPage - 1) * pageSize + index + 1}</td>
                      <td className={masterTableID}>{item.supplier_id}</td>
                      <td className={masterTableData}>{item.supplier_name}</td>
                      <td className={masterTableData}>{item.contact_person || '-'}</td>
                      <td className={masterTableData}>{item.mobile_number || '-'}</td>
                      <td className={masterTableData}>{item.GST_number || '-'}</td>
                      <td className={masterTableData}>{item.drug_license_number || '-'}</td>
                      <td className={masterTableData}>
                        <span className={`px-2 rounded text-[9px] ${item.status === 'Active' ? 'bg-emerald-100 text-emerald-600' : 'bg-rose-100 text-rose-600'}`}>
                          {item.status}
                        </span>
                      </td>
                      <td className={masterTableData}>
                        <div className="flex justify-center gap-2">
                          <button onClick={() => handleEdit(item)} className="p-1 text-emerald-600 rounded-lg hover:bg-emerald-100"><Edit size={14} /></button>
                          <button onClick={() => handleDelete(item._id)} className="p-1 text-rose-600 rounded-lg hover:bg-rose-100"><Trash2 size={14} /></button>
                        </div>
                      </td>
                    </tr>
                  )) : (
                    <tr><td colSpan="9" className="p-12 text-center text-slate-300 font-medium uppercase text-xs">No matching records found</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

const Input = ({ label, required, ...props }) => (
  <div className="flex flex-col">
    <label className={masterInputLabel}>{label}</label>
    <input required={required} {...props} className={masterInput} />
  </div>
);

const Select = ({ label, options, ...props }) => (
  <div className="flex flex-col">
    <label className={masterInputLabel}>{label}</label>
    <select {...props} className={masterSelect}>
      {options.map(opt => <option key={opt} value={opt}>{opt}</option>)}
    </select>
  </div>
);

export default SupplierMaster;
